import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NotificationContext = createContext(null);

/** Maintains a resilient WebSocket to /api/ws while a user is logged in. */
function useRealtimeSocket(userId, onEvent) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!userId) return undefined;
    let closed = false;
    let retry = 0;
    let ws = null;

    const connect = () => {
      const token = localStorage.getItem("vc_token");
      if (!token || closed) return;
      const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws?token=${encodeURIComponent(token)}`;
      ws = new WebSocket(wsUrl);
      ws.onopen = () => { retry = 0; };
      ws.onmessage = (e) => {
        try {
          onEventRef.current(JSON.parse(e.data));
        } catch (err) {
          console.warn("WS message parse failed", err);
        }
      };
      ws.onclose = () => {
        if (!closed && retry < 6) { retry += 1; setTimeout(connect, 2000 * retry); }
      };
    };
    connect();
    return () => {
      closed = true;
      try { ws?.close(); } catch (err) { console.warn("WS close failed", err); }
    };
  }, [userId]);
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ notifications: 0, messages: 0 });
  const dmListeners = useRef(new Set());

  const refreshCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCounts(data);
    } catch (err) {
      console.warn("Failed to refresh unread counts", err);
    }
  }, []);

  useEffect(() => {
    if (!user) { setCounts({ notifications: 0, messages: 0 }); return undefined; }
    refreshCounts();
    const iv = setInterval(refreshCounts, 30000);
    return () => clearInterval(iv);
  }, [user, refreshCounts]);

  const handleEvent = useCallback((data) => {
    if (data.type === "dm") {
      let handled = false;
      dmListeners.current.forEach((fn) => { if (fn(data.message)) handled = true; });
      if (!handled) {
        setCounts((c) => ({ ...c, messages: c.messages + 1 }));
        toast.message(`New message from ${data.from_name || "someone"}`, {
          description: (data.message?.body || "").slice(0, 80),
        });
      }
    } else if (data.type === "notification") {
      setCounts((c) => ({ ...c, notifications: c.notifications + 1 }));
      toast.message(data.notification?.title, {
        description: (data.notification?.body || "").slice(0, 100),
      });
    }
  }, []);

  useRealtimeSocket(user?.user_id, handleEvent);

  const subscribeDm = useCallback((fn) => {
    dmListeners.current.add(fn);
    return () => dmListeners.current.delete(fn);
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setCounts((c) => ({ ...c, notifications: 0 }));
    } catch (err) {
      console.warn("Failed to mark notifications read", err);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{ counts, subscribeDm, markAllRead, refreshCounts }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
